import kebabCase from 'just-kebab-case'
import { type DirectoryWithIcons, type IconWithId } from './misc.js'

/**
 * Filters icons by subPath using kebab-case comparison.
 */
export function filterIconsBySubPath<T extends { path?: string | null }>(
	icons: T[],
	subPath: string | null,
): T[] {
	if (!subPath) return icons
	return icons.filter((icon) => {
		const iconPathKebab = icon.path?.split('/').map(kebabCase).join('/')
		return iconPathKebab?.startsWith(subPath)
	})
}

/**
 * Converts flat icons array to DirectoryWithIcons array.
 * When grouped=true, creates separate directories for each unique path.
 * When grouped=false (default), returns single directory with all icons.
 */
export function iconsToDirectories(
	icons: IconWithId[],
	iconSetName: string,
	iconSetSlug: string,
	grouped?: boolean,
): DirectoryWithIcons[] {
	if (!grouped) {
		// Flat: single directory with all icons
		const sortedIcons = [...icons].sort((a, b) => a.name.localeCompare(b.name))
		return [
			{
				path: iconSetSlug,
				breadcrumbs: [iconSetName],
				icons: sortedIcons,
			},
		]
	}

	// Grouped: separate directories by path
	const directoriesMap = new Map<string, DirectoryWithIcons>()

	for (const icon of icons) {
		const subPath = icon.path || ''
		const pathSegments = subPath.split('/').filter(Boolean)
		const kebabPathSegments = pathSegments.map(kebabCase)
		const directoryPath =
			kebabPathSegments.length > 0
				? `${iconSetSlug}/${kebabPathSegments.join('/')}`
				: iconSetSlug
		const breadcrumbs =
			pathSegments.length > 0 ? [iconSetName, ...pathSegments] : [iconSetName]

		let directory = directoriesMap.get(directoryPath)
		if (!directory) {
			directory = {
				path: directoryPath,
				breadcrumbs,
				icons: [],
			}
			directoriesMap.set(directoryPath, directory)
		}

		directory.icons.push(icon)
	}

	return Array.from(directoriesMap.values())
		.map((directory) => ({
			...directory,
			icons: directory.icons.sort((a, b) => a.name.localeCompare(b.name)),
		}))
		.sort((a, b) => a.path.localeCompare(b.path))
}
